import { z } from "zod";
import { db } from "../../../core/data/local/db";
import type { ItemKind } from "../domain/earn";

/**
 * The till's copy of every card (specs/006-loyalty research R7).
 *
 * ONE DOCUMENT IN `meta`, like inventory's recipes: no Dexie store named for a
 * module. Keyed by DIGEST, never by code — the replica is the full list of
 * valid cards, and a dumped tablet must not yield one anyone could print.
 *
 * WHAT THE REPLICA MAY NOT YET KNOW. A refresh started at `fetchStartedAt`
 * cannot include anything this till recorded after that, nor anything whose
 * envelope was still waiting in the outbox when it started (`pendingAtFetch`).
 * Those records are added on top locally (records.ts) until a later refresh
 * has certainly seen them — so a reward redeemed a moment ago can never look
 * available again while the copy catches up.
 */

export interface ReplicaCard {
  status: "active" | "blocked" | "retired";
  L: number;
  R: number;
}

export interface LoyaltyReplica {
  seq: number;
  cards: Record<string, ReplicaCard>;
  rules: Record<string, ItemKind>;
  fetchStartedAt: string;
  pendingAtFetch: string[];
}

export const REPLICA_META_KEY = "loyalty.replica";
export const LOYALTY_ENVELOPES = ["loyalty.earn", "loyalty.reverse"];

const cardSchema = z.object({
  status: z.enum(["active", "blocked", "retired"]),
  L: z.number().int(),
  R: z.number().int(),
});

const replicaSchema = z.object({
  seq: z.number().int(),
  cards: z.record(z.string(), cardSchema),
  rules: z.record(z.string(), z.enum(["drink", "pastry", "none"])),
  fetchStartedAt: z.string(),
  pendingAtFetch: z.array(z.string()),
});

const pageSchema = z.object({
  seq: z.number().int(),
  more: z.boolean(),
  cards: z.array(cardSchema.extend({ digest: z.string() })),
  rules: z.array(z.object({ menuItemId: z.string(), kind: z.enum(["drink", "pastry", "none"]) })),
});

const EMPTY: LoyaltyReplica = { seq: -1, cards: {}, rules: {}, fetchStartedAt: "", pendingAtFetch: [] };

let cached: LoyaltyReplica | null = null;

export async function loadReplica(): Promise<LoyaltyReplica> {
  if (cached) return cached;
  const row = await db.meta.get(REPLICA_META_KEY);
  const parsed = replicaSchema.safeParse(row?.value);
  cached = parsed.success ? parsed.data : EMPTY;
  return cached;
}

export function forgetReplicaCache(): void {
  cached = null;
}

async function pendingLoyaltyEnvelopes(): Promise<string[]> {
  // The outbox is not indexed by type, and holds only what has not synced yet.
  const rows = await db.outbox.filter((row) => LOYALTY_ENVELOPES.includes(row.type)).toArray();
  return rows.map((row) => row.opId);
}

/**
 * Pull what changed since this till's cursor, page by page. Offline or
 * refused is not an error: the copy already here keeps working.
 */
export async function refreshLoyaltyReplica(sessionToken: string): Promise<boolean> {
  const current = await loadReplica();
  const fetchStartedAt = new Date().toISOString();
  const pendingAtFetch = await pendingLoyaltyEnvelopes();

  const cards = { ...current.cards };
  let rules = current.rules;
  let seq = current.seq;

  for (let page = 0; page < 50; page += 1) {
    let response: Response;
    try {
      response = await fetch(`/api/loyalty/replica?since=${seq}`, {
        headers: { authorization: `Bearer ${sessionToken}` },
      });
    } catch {
      return false;
    }
    if (!response.ok) return false;

    const body = pageSchema.parse(await response.json());
    for (const card of body.cards) cards[card.digest] = { status: card.status, L: card.L, R: card.R };
    rules = Object.fromEntries(body.rules.map((rule) => [rule.menuItemId, rule.kind]));
    seq = body.seq;
    if (!body.more) break;
  }

  const next: LoyaltyReplica = { seq, cards, rules, fetchStartedAt, pendingAtFetch };
  await db.meta.put({ key: REPLICA_META_KEY, value: next });
  cached = next;
  return true;
}
